import { Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { formatarQuantidade } from "@/lib/quantidade";
import { formatPriceCents } from "@/components/services/service-meta";
import { formatDate } from "@/components/prosthesis/prosthesis-meta";

const ROTULO: Record<string, string> = {
  INVENTARIO: "Inventário",
  COMPRA: "Compra",
  CONSUMO: "Consumo",
  AJUSTE: "Ajuste",
};

/**
 * Os lançamentos de um material.
 *
 * É esta tela que justifica a escolha de razão imutável: o saldo não é um número que alguém
 * digitou, é a soma do que está aqui. Quando o protético diz "o sistema está errado", a resposta
 * está nesta lista — e cada saída aponta para o paciente que a causou.
 */
export function ExtratoDeEstoque({ materialLineId }: { materialLineId: string }) {
  const query = trpc.estoque.extrato.useQuery({ materialLineId, take: 100, skip: 0 });

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-brand-600" />
      </div>
    );
  }
  const dados = query.data;
  if (!dados) return null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2 rounded-lg bg-zinc-50 px-3 py-2 dark:bg-muted/40">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-zinc-900 dark:text-foreground">{dados.material.name}</p>
          <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">{dados.material.manufacturerName}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">Saldo</p>
          <p
            className={cn(
              "text-sm font-semibold tabular-nums",
              dados.saldo < 0 ? "text-red-600 dark:text-red-400" : "text-zinc-900 dark:text-foreground",
            )}
          >
            {formatarQuantidade(dados.saldo)}
            {dados.material.unit && <span className="ml-0.5 text-zinc-400">{dados.material.unit}</span>}
          </p>
        </div>
      </div>

      {dados.lancamentos.length === 0 ? (
        <p className="py-8 text-center text-xs text-zinc-400 dark:text-muted-foreground">
          Nenhum lançamento ainda. O saldo começa a se formar quando um serviço com este insumo for
          concluído, ou quando uma compra for registrada.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-xs">
            <thead className="border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 dark:border-border dark:text-muted-foreground">
              <tr>
                <th className="w-24 pb-1.5 pr-2 text-left font-semibold">Data</th>
                <th className="w-24 pb-1.5 pr-2 text-left font-semibold">Tipo</th>
                <th className="pb-1.5 pr-2 text-left font-semibold">Origem</th>
                <th className="w-24 pb-1.5 pr-2 text-right font-semibold">Quantidade</th>
              </tr>
            </thead>
            <tbody>
              {dados.lancamentos.map((l: any) => (
                <tr key={l.id} className="border-b border-zinc-100 last:border-b-0 dark:border-border/50">
                  <td className="py-1.5 pr-2 tabular-nums text-zinc-500 dark:text-muted-foreground">
                    {formatDate(l.createdAt)}
                  </td>
                  <td className="py-1.5 pr-2 text-zinc-700 dark:text-foreground/80">{ROTULO[l.kind] ?? l.kind}</td>
                  <td className="py-1.5 pr-2 text-zinc-600 dark:text-muted-foreground">
                    <span className="block truncate">
                      {l.paciente ?? l.documento ?? l.notes ?? "—"}
                    </span>
                    {l.porQuem && (
                      <span className="text-[10px] text-zinc-400 dark:text-muted-foreground/70">por {l.porQuem}</span>
                    )}
                  </td>
                  <td
                    className={cn(
                      "py-1.5 pr-2 text-right font-medium tabular-nums",
                      l.quantity < 0 ? "text-red-600 dark:text-red-400" : "text-emerald-700 dark:text-emerald-400",
                    )}
                  >
                    {l.quantity > 0 ? "+" : ""}
                    {formatarQuantidade(l.quantity)}
                    {l.unitCostCents != null && (
                      <span className="ml-1 text-[10px] font-normal text-zinc-400">
                        {formatPriceCents(l.unitCostCents)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dados.total > dados.lancamentos.length && (
        <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
          Mostrando os {dados.lancamentos.length} mais recentes de {dados.total}.
        </p>
      )}
    </div>
  );
}
